import { useState, useEffect, useRef, useMemo, useCallback, memo } from 'react';
import ReactDOM from 'react-dom';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import Icon from '../common/Icon';
import AgGrid from '../common/AgGrid';
import * as XLSX from 'xlsx';
import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';
import { AisScreenLoader } from '../../utils/AisScreenLoader';
import {
  createPackageCosts,
  DeletePackageCosts,
  GetPackageCost,
  updatePackageCosts,
  CostTypePackageCost
} from '../../services/accountService';
import SuccessMessage from '../common/SuccessMessage';
const formatDisplayValue = (value) => {
  if (value === null || value === undefined || value === '' || value === 0) return '';
  return value;
};

const formatNumber = (value) => {
  if (value === null || value === undefined) return '-';
  return Number(value).toLocaleString();
};
const CostTypeDropdown = memo(({
  value,
  id,
  costTypeOptions,
  isEditMode,
  onFieldChange
}) => {
  const [showDropdown, setShowDropdown] = useState(false);
  const [searchTerm, setSearchTerm] = useState(value || '');
  const wrapperRef = useRef(null);
  const inputRef = useRef(null);
  const panelRef = useRef(null);
  const [dropdownPos, setDropdownPos] = useState({ top: 0, left: 0, width: 0, openUp: false, maxHeight: 250 });
  useEffect(() => {
    setSearchTerm(value || '');
  }, [value]);

  const updateDropdownPosition = useCallback(() => {
    if (!inputRef.current) return;
    const rect = inputRef.current.getBoundingClientRect();
    const PANEL_HEIGHT = 250;
    const MARGIN = 8;

    const spaceBelow = window.innerHeight - rect.bottom - MARGIN;
    const spaceAbove = rect.top - MARGIN;


    const openUp = spaceBelow < PANEL_HEIGHT && spaceAbove > spaceBelow;

    setDropdownPos({
      top: openUp ? rect.top - 2 : rect.bottom + 2,
      left: rect.left,
      width: rect.width,
      openUp,
      maxHeight: Math.max(120, Math.min(PANEL_HEIGHT, openUp ? spaceAbove : spaceBelow))
    });
  }, []);
  useEffect(() => {
    if (!showDropdown) return;
    const handleClickOutside = (e) => {
      const clickedInWrapper = wrapperRef.current && wrapperRef.current.contains(e.target);
      const clickedInPanel = panelRef.current && panelRef.current.contains(e.target);
      if (!clickedInWrapper && !clickedInPanel) setShowDropdown(false);
    };
    const handleReposition = () => updateDropdownPosition();
    document.addEventListener('mousedown', handleClickOutside);
    window.addEventListener('scroll', handleReposition, true);
    window.addEventListener('resize', handleReposition);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      window.removeEventListener('scroll', handleReposition, true);
      window.removeEventListener('resize', handleReposition);
    };
  }, [showDropdown, updateDropdownPosition]);

  if (!isEditMode) return <span>{value || ''}</span>;

  const filteredOptions = (costTypeOptions || []).filter(opt =>
    opt.costTypeName.toLowerCase().includes((searchTerm || '').toLowerCase())
  );

  const openDropdown = () => { updateDropdownPosition(); setShowDropdown(true); };

  const handleSelectOption = (option) => {
    onFieldChange(id, 'packageName', option.costTypeName);
    onFieldChange(id, 'costTypeID', option.costTypeID);
    onFieldChange(id, 'costCategoryID', option.costCategoryID);
    setSearchTerm(option.costTypeName);
    setShowDropdown(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);
    if (!showDropdown) openDropdown();
    onFieldChange(id, 'packageName', newValue);
    onFieldChange(id, 'costTypeID', null);
  };

  const handleInputBlur = () => {
    const match = (costTypeOptions || []).find(
      opt => opt.costTypeName.toLowerCase() === (searchTerm || '').toLowerCase()
    );
    if (match) {
      onFieldChange(id, 'packageName', match.costTypeName);
      onFieldChange(id, 'costTypeID', match.costTypeID);
      onFieldChange(id, 'costCategoryID', match.costCategoryID);
    }
  };

  const isInvalid = !searchTerm || searchTerm.trim() === '';

  const dropdownPanel = showDropdown ? ReactDOM.createPortal(
    <div
      ref={panelRef}
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        position: 'fixed',
        top: dropdownPos.top,
        left: dropdownPos.left,
        width: dropdownPos.width,
        minWidth: '200px',
        backgroundColor: '#fff',
        border: '1px solid #ccc',
        borderRadius: '4px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
        zIndex: 99999,
        maxHeight: dropdownPos.maxHeight,
        overflowY: 'auto',
        transform: dropdownPos.openUp ? 'translateY(-100%)' : 'none'
      }}
    >
      {filteredOptions.length > 0 ? (
        filteredOptions.map((option) => (
          <div
            key={option.costTypeID}
            onMouseDown={(e) => { e.preventDefault(); handleSelectOption(option); }}
            style={{
              padding: '8px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
              backgroundColor: searchTerm === option.costTypeName ? '#e6f7ff' : '#fff',
              fontSize: '13px', transition: 'background-color 0.15s'
            }}
            onMouseEnter={(e) => { if (searchTerm !== option.costTypeName) e.currentTarget.style.backgroundColor = '#f5f5f5'; }}
            onMouseLeave={(e) => { if (searchTerm !== option.costTypeName) e.currentTarget.style.backgroundColor = '#fff'; }}
          >
            {option.costTypeName}
          </div>
        ))
      ) : (
        <div style={{ padding: '10px 12px', color: '#666', fontSize: '13px', fontStyle: 'italic' }}>
          {searchTerm ? `No match — "${searchTerm}" will be added as new` : 'No cost types available'}
        </div>
      )}
    </div>,
    document.body
  ) : null;
  return (
    <div
      ref={wrapperRef}
      style={{ position: 'relative', width: '100%' }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
    >
      <input
        ref={inputRef}
        type="text"
        value={searchTerm}
        placeholder="Select or Add *"
        onChange={handleInputChange}
        onFocus={openDropdown}
        onClick={openDropdown}
        onBlur={handleInputBlur}
        style={{
          width: '100%', padding: '6px 8px',
          border: `1px solid ${isInvalid ? '#dc3545' : '#007bff'}`,
          borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
          backgroundColor: isInvalid ? '#fff3f3' : 'white',
          outline: 'none', boxSizing: 'border-box', cursor: 'text'
        }}
      />
      {dropdownPanel}
    </div>
  );
});
CostTypeDropdown.displayName = 'CostTypeDropdown';
const EditableInputRenderer = memo(({
  value, id, field, type = 'text', isCurrency = false,
  placeholder = '', isEditMode, onFieldChange
}) => {
  const [localValue, setLocalValue] = useState(formatDisplayValue(value));

  useEffect(() => { setLocalValue(formatDisplayValue(value)); }, [value]);

  if (!isEditMode) {
    if (isCurrency) {
      const displayValue = (value === null || value === undefined || value === '') ? 0 : Number(value);
      return <span>${displayValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
    }
    if (type === 'number') {
      const displayValue = (value === null || value === undefined || value === '') ? '' : value;
      return <span>{displayValue === '' ? '' : Number(displayValue).toLocaleString()}</span>;
    }
    return <span>{value || ''}</span>;
  }

  const isRequiredField = field === 'packageName';
  const isInvalid = isRequiredField && (!localValue || localValue === '');
  const isNewRow = id && String(id).startsWith('temp-');

  return (
    <input
      type={type}
      value={localValue === null || localValue === undefined ? '' : localValue}
      placeholder={placeholder || (isRequiredField ? 'Required *' : '')}
      onChange={(e) => {
        let newValue = e.target.value;
        if (type === 'number') newValue = newValue === '' ? '' : parseFloat(newValue);
        setLocalValue(newValue);
      }}
      onBlur={() => {
        let finalValue = localValue;
        if (type === 'number') finalValue = (localValue === '' || localValue === null || localValue === undefined) ? '' : parseFloat(localValue);
        if (String(finalValue) !== String(value ?? '')) onFieldChange(id, field, finalValue);
      }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '100%', padding: '6px 8px',
        border: `1px solid ${isEditMode ? (isInvalid && isNewRow ? '#dc3545' : '#007bff') : '#d0d0d0'}`,
        borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
        textAlign: isCurrency ? 'right' : 'left',
        backgroundColor: isInvalid && isNewRow ? '#fff3f3' : 'white',
        outline: 'none', transition: 'border-color 0.2s'
      }}
      step={type === 'number' ? "0.01" : undefined}
    />
  );
});
EditableInputRenderer.displayName = 'EditableInputRenderer';
const MFRateDropdown = memo(({ value, id, field, isEditMode, onFieldChange }) => {
  const currentValue = value === true || value === 'M' ? 'M' : 'F';
  if (!isEditMode) return <span style={{ textAlign: 'center', display: 'block' }}>{currentValue}</span>;
  return (
    <select
      value={currentValue}
      onChange={(e) => onFieldChange(id, field, e.target.value)}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      style={{
        width: '80px', padding: '6px 8px', border: '1px solid #007bff',
        borderRadius: '4px', fontSize: '13px', fontFamily: 'inherit',
        textAlign: 'center', cursor: 'pointer', backgroundColor: 'white'
      }}
    >
      <option value="M">M</option>
      <option value="F">F</option>
    </select>
  );
});
MFRateDropdown.displayName = 'MFRateDropdown';
const StatusDropdownCell = memo(({ data, isEditMode, onStatusChange }) => {
  const currentValue = data?.isCostFinal === true ? 'Yes' : 'No';
  const packageId = data?.jobCostID || data?.tempId;
  if (!packageId || data?.isTotalsRow || data?.isAddRow) return <span></span>;
  if (!isEditMode) return <span>{currentValue}</span>;
  return (
    <select
      value={currentValue}
      onChange={(e) => { e.stopPropagation(); onStatusChange(packageId, e.target.value === 'Yes'); }}
      onMouseDown={(e) => e.stopPropagation()}
      onClick={(e) => e.stopPropagation()}
      disabled={!isEditMode}
      style={{
        width: '100%', padding: '6px 10px',
        border: `1px solid ${isEditMode ? '#007bff' : '#d0d0d0'}`,
        borderRadius: '4px', backgroundColor: isEditMode ? '#ffffff' : '#f8f9fa',
        cursor: isEditMode ? 'pointer' : 'default', fontSize: '13px',
        fontFamily: 'inherit', fontWeight: '500', outline: 'none'
      }}
    >
      <option value="Yes">Yes</option>
      <option value="No">No</option>
    </select>
  );
});
StatusDropdownCell.displayName = 'StatusDropdownCell';
const CurrencyRenderer = ({ value, isTotal = false }) => {
  if (value === null || value === undefined || value === '') return <span>-</span>;
  const displayValue = Number(value);
  if (isTotal) return <span style={{ fontWeight: 'bold' }}>${displayValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
  return <span>${displayValue.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>;
};

const VarianceRenderer = ({ value, isTotal = false }) => {
  if (value === null || value === undefined) return <span>-</span>;
  const symbol = value < 0 ? '-' : '';
  const formatted = `${symbol}$${Math.abs(value).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  if (isTotal) return <span style={{ fontWeight: 'bold' }}>{formatted}</span>;
  return <span>{formatted}</span>;
};

const VariancePercentRenderer = ({ value, isTotal = false }) => {
  if (value === null || value === undefined) return <span>-</span>;
  const symbol = value < 0 ? '-' : '';
  const formatted = `${symbol}${Math.abs(value).toFixed(2)}%`;
  if (isTotal) return <span style={{ fontWeight: 'bold' }}>{formatted}</span>;
  return <span>{formatted}</span>;
};

const PackageCostsDetails = () => {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  const [loading, setLoading] = useState(false);
  const [packageData, setPackageData] = useState([]);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [isManageComponentsActive, setIsManageComponentsActive] = useState(false);
  const [gridApi, setGridApi] = useState(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedCostType, setSelectedCostType] = useState('package');
  const [showCostTypeDropdown, setShowCostTypeDropdown] = useState(false);

  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('success');

  const clientId = location.state?.clientId;

  const [navigationSource, setNavigationSource] = useState(() => {
    if (location.state?.source) return location.state.source;
    const stored = sessionStorage.getItem('jobDetailsSource');
    return stored || 'job-module';
  });

  const [isAllClientsFlow, setIsAllClientsFlow] = useState(() => {
    if (location.state?.isAllClients !== undefined) {
      return Boolean(location.state.isAllClients);
    }
    return sessionStorage.getItem('isAllClientsFlow') === 'true';
  });

  const [cameFromIntegration, setCameFromIntegration] = useState(() => {
    if (location.state?.fromIntegration !== undefined) {
      return Boolean(location.state.fromIntegration);
    }
    return sessionStorage.getItem('cameFromIntegration') === 'true';
  });


  const [reportsContext, setReportsContext] = useState(() => {
    if (location.state?.fromReports) {
      return {
        fromReports: true,
        reportFilters: location.state.reportFilters || {},
        selectedClientId: location.state.selectedClientId || [],
        reportType: location.state.reportType || '',
        rowData: location.state.rowData || []
      };
    }
    const stored = sessionStorage.getItem('reportsContext');
    return stored ? JSON.parse(stored) : null;
  });

  const showReportsCrumb =

    reportsContext?.fromReports &&

    navigationSource === 'reports';

  useEffect(() => {
    if (location.state?.fromReports) {
      const ctx = {
        fromReports: true,
        reportFilters: location.state.reportFilters || {},
        selectedClientId: location.state.selectedClientId || [],
        reportType: location.state.reportType || '',
        rowData: location.state.rowData || []
      };
      sessionStorage.setItem('reportsContext', JSON.stringify(ctx));
      setReportsContext(ctx);
    }
  }, [location.state]);


  useEffect(() => {
    if (location.state?.source) {
      sessionStorage.setItem('jobDetailsSource', location.state.source);
      setNavigationSource(location.state.source);
    }
    if (location.state?.isAllClients !== undefined) {
      sessionStorage.setItem(
        'isAllClientsFlow',
        String(Boolean(location.state.isAllClients))
      );
      setIsAllClientsFlow(Boolean(location.state.isAllClients));
    }
  }, [location.state]);


  const showClientSelectCrumb =
    navigationSource === 'dashboard' ||
    navigationSource === 'client-dashboard' ||
    isAllClientsFlow;

  const showClientNameCrumb =
    (navigationSource === 'dashboard' ||
      navigationSource === 'client-dashboard') &&
    !isAllClientsFlow;





  const agGridRef = useRef(null);


  const gridApiRef = useRef(null);


  const menuDropdownRef = useRef(null);

  const [costTypeOptions, setCostTypeOptions] = useState([]);
  const originalDataRef = useRef([]);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [selectedCostsToDelete, setSelectedCostsToDelete] = useState([]);
  const [deleteInProgress, setDeleteInProgress] = useState(false);

  const [filteredPackageData, setFilteredPackageData] = useState(null);

  const activeJobId = jobId || location.state?.jobId;
  const jobCode = location.state?.jobCode;
  const clientName = location.state?.clientName;

  const fetchCostTypeOptions = async (costCategoryID) => {
    try {
      const categoryID = Array.isArray(costCategoryID) ? costCategoryID[0] : costCategoryID;
      const payload = [Number(categoryID)];
      const response = await CostTypePackageCost(payload);
      let data = [];
      if (Array.isArray(response)) data = response;
      else if (response && Array.isArray(response.data)) data = response.data;
      else if (response && Array.isArray(response.items)) data = response.items;
      setCostTypeOptions(data);
      return data;
    } catch (error) {
      setCostTypeOptions([]);
      return [];
    }
  };

  const resolveCostCategoryID = useCallback(() => {
    const rowWithCategory = packageData.find(r => r.costCategoryID && !r.isAddRow && !r.isTotalsRow);
    return rowWithCategory?.costCategoryID ?? 1;
  }, [packageData]);

  const fetchLatestPackageCosts = async () => {
    if (!activeJobId) return [];
    try {
      setLoading(true);
      const response = await GetPackageCost(activeJobId);
      let data = [];
      if (response && Array.isArray(response)) data = response;
      else if (response && response.data && Array.isArray(response.data)) data = response.data;
      else if (response && response.items && Array.isArray(response.items)) data = response.items;
      else { console.warn('Unexpected API response structure:', response); data = []; }
      return data;
    } catch (error) {
      console.error('Error fetching latest package costs:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const transformPackageData = (apiData) => {
    if (!Array.isArray(apiData)) return [];
    return apiData.map(item => ({
      jobCostID: item.jobCostID,
      costCategoryID: item.costCategoryID,
      costTypeID: item.costTypeID,
      packageName: item.costComponentName || item.displayName || item.costTypeName || '',
      packageDescription: item.costComponentDescription || '',
      poNumber: item.poNumber || '',
      vendor: item.vendor || '',
      invoiceNumber: item.invoiceNumber || '',
      estimatedQuantity: item.estimatedQuantity !== null && item.estimatedQuantity !== undefined ? item.estimatedQuantity : '',
      estimatedCost: item.estimatedCost !== null && item.estimatedCost !== undefined && item.estimatedCost !== 0 ? item.estimatedCost : '',
      estimatedRateType: item.isEstimatedCostPerThousand === true ? 'M' : 'F',
      totalEstimatedCost: item.totalEstCost || 0,
      actualQuantity: item.actualQuantity !== null && item.actualQuantity !== undefined ? item.actualQuantity : '',
      actualCost: item.actualCost !== null && item.actualCost !== undefined && item.actualCost !== 0 ? item.actualCost : '',
      actualRateType: item.isActualCostPerThousand === true ? 'M' : 'F',
      totalActualCost: item.totalActualCost !== null && item.totalActualCost !== undefined ? item.totalActualCost : '',
      preInvoiceNumber: item.preInvoiceNumber || '',
      preInvoiceCost: item.preInvoiceCost !== null && item.preInvoiceCost !== undefined && item.preInvoiceCost !== 0 ? item.preInvoiceCost : '',
      clientRateType: item.isPreInvoiceCostPerThousand === true ? 'M' : 'F',
      totalClientEstCost: item.totalClientEstCost || 0,
      variance: item.costVariance ?? 0,
      variancePercent: item.costVariancePercentage ?? 0,
      isCostFinal: item.isCostFinal ?? false,
      mailCodeCount: item.mailCodeCount || 0,
      jobID: item.jobID,
      status: item.status,
      createdBy: item.createdBy,
      createdDate: item.createdDate,
      modifiedBy: item.modifiedBy,
      modifiedDate: item.modifiedDate,
      costCategoryName: item.costCategoryName,
      costTypeName: item.costTypeName,
      displayName: item.displayName,
      costComponentName: item.costComponentName,
      costComponentDescription: item.costComponentDescription,
      actulaCostMFCalc: item.actulaCostMFCalc || 0,
      isPreInvoiceCostPerThousand: item.isPreInvoiceCostPerThousand || false,
      isEstimatedCostPerThousand: item.isEstimatedCostPerThousand || false,
      isActualCostPerThousand: item.isActualCostPerThousand || false
    }));
  };

 const handleStatusChange = useCallback((jobCostID, newValue) => {
    if (!isEditMode) return;
    setPackageData(prevData =>
      prevData.map(row => {
        const isMatch = row.jobCostID === jobCostID || row.tempId === jobCostID;
        return isMatch ? { ...row, isCostFinal: newValue } : row;
      })
    );
  }, [isEditMode]);

  const initializePackageData = async () => {
    try {
      setLoading(true);
      const data = await fetchLatestPackageCosts();
      const transformedData = transformPackageData(data);
      setPackageData(transformedData);
      originalDataRef.current = JSON.parse(JSON.stringify(transformedData));
      return transformedData;
    } catch (error) {
      console.error('Error initializing package data:', error);
      setPackageData([]);
      originalDataRef.current = [];
      throw error;
    } finally {
      setLoading(false);
    }
  };

  const refreshDataFromAPI = async () => {
    try {
      setLoading(true);
      const latestData = await fetchLatestPackageCosts();
      const transformedData = transformPackageData(latestData);
      setPackageData(transformedData);
      originalDataRef.current = JSON.parse(JSON.stringify(transformedData));
      if (isManageComponentsActive) { setIsManageComponentsActive(false); setIsEditMode(false); }
      return transformedData;
    } catch (error) {
      console.error('Error refreshing data:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (activeJobId) initializePackageData();
  }, [activeJobId]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuDropdownRef.current && !menuDropdownRef.current.contains(event.target)) {
        setShowDropdownMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const isRowChanged = (current, original) => {
    if (!original) return true;
    const keysToCompare = [
      'packageName', 'packageDescription', 'estimatedQuantity', 'estimatedCost',
      'actualQuantity', 'actualCost', 'poNumber', 'vendor', 'invoiceNumber',
      'preInvoiceNumber', 'preInvoiceCost', 'isCostFinal',
      'clientRateType', 'estimatedRateType', 'actualRateType'
    ];
    return keysToCompare.some(key => String(current[key] ?? '') !== String(original[key] ?? ''));
  };

  const handleFieldChange = useCallback((jobCostID, field, newValue) => {
    if (!isEditMode) return;
    setPackageData(prevData =>
      prevData.map(row => {
        const isMatch = row.jobCostID === jobCostID ||
          (jobCostID === null && !row.jobCostID) ||
          row.tempId === jobCostID;
        if (isMatch) {
          let updatedRow = { ...row, [field]: newValue };
          if (field === 'clientRateType') updatedRow.isPreInvoiceCostPerThousand = newValue === 'M';
          if (field === 'estimatedRateType') updatedRow.isEstimatedCostPerThousand = newValue === 'M';
          if (field === 'actualRateType') updatedRow.isActualCostPerThousand = newValue === 'M';
          return updatedRow;
        }
        return row;
      })
    );
  }, [isEditMode]);

  const buildCreatePayload = (rows) => rows.map(row => ({
    jobCostID: null, jobID: Number(activeJobId),
    costCategoryID: row.costCategoryID || 1, costTypeID: row.costTypeID || null,
    costComponentName: row.packageName || '', costComponentDescription: row.packageDescription || '',
    estimatedCost: Number(row.estimatedCost) || 0, estimatedQuantity: Number(row.estimatedQuantity) || 0,
    poNumber: row.poNumber || '', vendor: row.vendor || '', status: true,
    actualCost: Number(row.actualCost) || 0, actualQuantity: Number(row.actualQuantity) || 0,
    invoiceNumber: row.invoiceNumber || '', preInvoiceCost: Number(row.preInvoiceCost) || 0,
    preInvoiceNumber: row.preInvoiceNumber || '', actulaCostMFCalc: row.actulaCostMFCalc || 0,
    isCostFinal: row.isCostFinal || false,
    isPreInvoiceCostPerThousand: row.isPreInvoiceCostPerThousand || false,
    isEstimatedCostPerThousand: row.isEstimatedCostPerThousand || false,
    isActualCostPerThousand: row.isActualCostPerThousand || false,
  }));

  const buildUpdatePayload = (rows) => ({
    jobID: Number(activeJobId),
    items: rows.map(row => ({
      jobCostID: row.jobCostID, jobID: Number(activeJobId),
      costCategoryID: row.costCategoryID, costTypeID: row.costTypeID,
      costComponentName: row.packageName || '', costComponentDescription: row.packageDescription || '',
      estimatedQuantity: Number(row.estimatedQuantity) || 0, estimatedCost: Number(row.estimatedCost) || 0,
      actualQuantity: Number(row.actualQuantity) || 0, actualCost: Number(row.actualCost) || 0,
      poNumber: row.poNumber || '', vendor: row.vendor || '', invoiceNumber: row.invoiceNumber || '',
      preInvoiceNumber: row.preInvoiceNumber || '', preInvoiceCost: Number(row.preInvoiceCost) || 0,
      actulaCostMFCalc: row.actulaCostMFCalc || 0, status: row.status !== undefined ? row.status : true,
      isCostFinal: row.isCostFinal || false,
      isPreInvoiceCostPerThousand: row.isPreInvoiceCostPerThousand || false,
      isEstimatedCostPerThousand: row.isEstimatedCostPerThousand || false,
      isActualCostPerThousand: row.isActualCostPerThousand || false,
    }))
  });

  const handleManageComponentsClick = () => {
    originalDataRef.current = JSON.parse(JSON.stringify(packageData));
    setIsManageComponentsActive(true);
    setIsEditMode(true);
  };

  const handleCancelClick = () => {
    setPackageData(JSON.parse(JSON.stringify(originalDataRef.current)));
    setIsManageComponentsActive(false);
    setIsEditMode(false);
  };

  const handleSaveChangesClick = async () => {
    if (gridApi) gridApi.stopEditing();
    try {
      setLoading(true);
      const rows = [];
      gridApi.forEachNode(node => {
        if (!node.data?.isTotalsRow && !node.data?.isAddRow) rows.push(node.data);
      });

      const newRows = rows.filter(r => !r.jobCostID || String(r.jobCostID).startsWith('temp-'));
      const existingRows = rows.filter(r => r.jobCostID && !String(r.jobCostID).startsWith('temp-'));

      const invalidRows = newRows.filter(row => !row.packageName || row.packageName.trim() === '');
      if (invalidRows.length > 0) {
        setMessage(`Please fill in the "Cost Type" field for ${invalidRows.length} new row(s) before saving`);
        setMessageType('error');
        setLoading(false);
        return;
      }

      if (newRows.length) {
        const createPayload = buildCreatePayload(newRows);
        await createPackageCosts(createPayload);
      }

      const changedRows = existingRows.filter(row => {
        const original = originalDataRef.current.find(o => o.jobCostID === row.jobCostID);
        return isRowChanged(row, original);
      });

      if (changedRows.length) {
        const updatePayload = buildUpdatePayload(changedRows);
        await updatePackageCosts(activeJobId, updatePayload);
      }

      await refreshDataFromAPI();
      setIsManageComponentsActive(false);
      setIsEditMode(false);
      setMessage('Changes saved successfully!');
      setMessageType('success');

    } catch (err) {
      console.error('Save failed:', err);
      setMessage('Save failed: ' + (err.message || 'Please try again'));
      setMessageType('error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteClick = () => {
    if (!gridApi) return;
    const selectedRows = gridApi.getSelectedRows();
    if (!selectedRows || selectedRows.length === 0) {
      setMessage('Please select components to delete');
      setMessageType('error');
      return;
    }
    setSelectedCostsToDelete(selectedRows);
    setShowDeleteConfirm(true);
  };

  const handleConfirmDelete = async () => {
    if (!selectedCostsToDelete.length) return;
    try {
      setDeleteInProgress(true);
      const apiIds = [];
      const tempRows = [];
      selectedCostsToDelete.forEach(row => {
        if (row.jobCostID && !String(row.jobCostID).startsWith('temp-')) apiIds.push(row.jobCostID);
        else tempRows.push(row);
      });
      if (tempRows.length > 0) setPackageData(prev => prev.filter(row => !tempRows.includes(row)));
      if (apiIds.length > 0) {
        await DeletePackageCosts(apiIds);
        await refreshDataFromAPI();
      }
      setShowDeleteConfirm(false);
      setSelectedCostsToDelete([]);
      setMessage('Deleted successfully');
      setMessageType('success');
    } catch (error) {
      console.error('Delete failed:', error);
      setMessage('Delete failed: ' + (error.message || 'Please try again'));
      setMessageType('error');
    } finally {
      setDeleteInProgress(false);
    }
  };

  const handleCancelDelete = () => {
    setShowDeleteConfirm(false);
    setSelectedCostsToDelete([]);
  };

  const handleAddNewRow = async () => {
    if (!gridApi) return;
    gridApi.stopEditing();
    const categoryID = resolveCostCategoryID();
    await fetchCostTypeOptions(categoryID);
    const tempId = `temp-${Date.now()}`;
    const newRow = {
      tempId, jobCostID: null, isNewRow: true, packageName: '',
      packageDescription: '', estimatedQuantity: '', estimatedCost: '',
      actualQuantity: '', actualCost: '', poNumber: '', vendor: '', invoiceNumber: '',
      actualRateType: 'F', totalEstimatedCost: 0, totalActualCost: '',
      variance: 0, variancePercent: 0, isCostFinal: false,
      preInvoiceNumber: '', preInvoiceCost: '', totalClientEstCost: 0,
      jobID: activeJobId, status: true,
      isEstimatedCostPerThousand: false, isActualCostPerThousand: false,
      isPreInvoiceCostPerThousand: false, costCategoryID: categoryID, costTypeID: null
    };
    setPackageData(prev => [...prev, newRow]);
  };

  const handleCellClicked = (params) => {
    if (params.data?.isAddRow && isManageComponentsActive) handleAddNewRow();
  };

  const calculateTotalsFromRows = useCallback((rows) => {
    if (!rows || rows.length === 0) {
      return { totalEstimatedCost: 0, totalActualCost: 0, totalClientEstCost: 0, variance: 0, variancePercent: 0, estimatedQuantity: 0, actualQuantity: 0 };
    }
    const validRows = rows.filter(r => !r.isTotalsRow && !r.isAddRow && !r.isNewRow);
    const totalEstimatedCost = validRows.reduce((sum, r) => sum + (Number(r.totalEstimatedCost) || 0), 0);
    const totalActualCost = validRows.reduce((sum, r) => sum + (Number(r.totalActualCost) || 0), 0);
    const totalClientEstCost = validRows.reduce((sum, r) => sum + (Number(r.totalClientEstCost) || 0), 0);
    const totalEstimatedQuantity = validRows.reduce(
      (max, r) => Math.max(max, Number(r.estimatedQuantity) || 0),
      0
    );
    const totalActualQuantity = validRows.reduce((sum, r) => (Number(r.actualQuantity) || 0), 0);
    const variance = totalActualCost - totalClientEstCost;
    const variancePercent = totalActualCost !== 0 ? ((totalActualCost - totalClientEstCost) * 100) / totalActualCost : 0;
    return { totalEstimatedCost, totalActualCost, totalClientEstCost, variance, variancePercent, estimatedQuantity: totalEstimatedQuantity, actualQuantity: totalActualQuantity };
  }, []);

  const scopedRows = filteredPackageData !== null ? filteredPackageData : packageData;

  const totalsRow = useMemo(() => {
    const totals = calculateTotalsFromRows(scopedRows);
    return {
      packageName: 'TOTAL', totalEstimatedCost: totals.totalEstimatedCost,
      totalActualCost: totals.totalActualCost, totalClientEstCost: totals.totalClientEstCost,
      variance: totals.variance, variancePercent: totals.variancePercent,
      estimatedQuantity: totals.estimatedQuantity, actualQuantity: totals.actualQuantity,
      isTotalsRow: true
    };
  }, [scopedRows, calculateTotalsFromRows]);

  const handleFilterChanged = useCallback((params) => {
    const api = params?.api || gridApiRef.current;
    if (!api || typeof api.forEachNodeAfterFilter !== 'function') return;

    const allCount = packageData.filter(r => !r.isTotalsRow && !r.isAddRow).length;
    const rows = [];
    api.forEachNodeAfterFilter(node => {
      if (node.data && !node.data.isTotalsRow && !node.data.isAddRow) rows.push(node.data);
    });

    if (rows.length < allCount) {
      setFilteredPackageData(rows);
    } else {
      setFilteredPackageData(null);
    }
  }, [packageData]);

  const rowDataWithTotals = useMemo(() => {
    const rows = packageData.filter(r => !r.isAddRow && !r.isTotalsRow);
    if (isManageComponentsActive) rows.push({ packageName: '+ New Component', isAddRow: true });
    return rows;
  }, [packageData, isManageComponentsActive]);


  const formatValueForField = (field, value) => {
    if (value === null || value === undefined) {
      const currencyFields = ['preInvoiceCost', 'totalClientEstCost', 'estimatedCost', 'totalEstimatedCost', 'actualCost', 'totalActualCost', 'variance'];
      if (currencyFields.includes(field)) return '$0.00';
      if (field === 'estimatedQuantity' || field === 'actualQuantity' || field === 'actulaCostMFCalc') return '0';
      if (field === 'variancePercent') return '0.00%';
      return '';
    }
    const currencyFields = ['preInvoiceCost', 'totalClientEstCost', 'estimatedCost', 'totalEstimatedCost', 'actualCost', 'totalActualCost'];
    const numericFields = ['estimatedQuantity', 'actualQuantity', 'actulaCostMFCalc'];
    const rateTypeFields = ['clientRateType', 'estimatedRateType', 'actualRateType'];
    if (field === 'isCostFinal') return value === true ? 'Yes' : 'No';
    if (field === 'variancePercent') {
      const n = Number(value);
      if (isNaN(n)) return String(value);
      return `${n < 0 ? '-' : ''}${Math.abs(n).toFixed(2)}%`;
    }
    if (field === 'variance') {
      const n = Number(value);
      if (isNaN(n)) return String(value);
      return `${n < 0 ? '-' : ''}$${Math.abs(n).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    if (currencyFields.includes(field)) {
      const n = Number(value);
      if (isNaN(n)) return String(value);
      return '$' + n.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
    if (numericFields.includes(field)) {
      const n = Number(value);
      return isNaN(n) ? String(value) : n.toLocaleString();
    }
    if (rateTypeFields.includes(field)) return value === true || value === 'M' ? 'M' : 'F';
    return String(value);
  };

  const allExportColumns = useMemo(() => ([
    { field: 'packageName', headerName: 'Cost Type' },
    { field: 'packageDescription', headerName: 'Description' },
    { field: 'preInvoiceNumber', headerName: 'Client Est. Inv #' },
    { field: 'preInvoiceCost', headerName: 'Client Estimate' },
    { field: 'clientRateType', headerName: 'M/F (Client)' },
    { field: 'totalClientEstCost', headerName: 'Total Client Est. Cost' },
    { field: 'poNumber', headerName: 'PO Number' },
    { field: 'vendor', headerName: 'Vendor' },
    { field: 'invoiceNumber', headerName: 'Invoice Number' },
    { field: 'estimatedQuantity', headerName: 'Est. Quantity' },
    { field: 'estimatedCost', headerName: 'Est. Cost' },
    { field: 'estimatedRateType', headerName: 'M/F (Est)' },
    { field: 'totalEstimatedCost', headerName: 'Total Est. Cost' },
    { field: 'actualQuantity', headerName: 'Act. Quantity' },
    { field: 'actualCost', headerName: 'Act. Cost' },
    { field: 'actualRateType', headerName: 'M/F (Act)' },
    { field: 'totalActualCost', headerName: 'Total Act. Cost' },
    { field: 'isCostFinal', headerName: 'Status' },
    { field: 'actulaCostMFCalc', headerName: '/M' },
    { field: 'variance', headerName: 'Cost Variance' },
    { field: 'variancePercent', headerName: 'Cost Variance %' },
  ]), []);

  const getExportColumns = useCallback(() => {
    if (agGridRef.current && typeof agGridRef.current.getVisibleColumns === 'function') {
      const visible = agGridRef.current.getVisibleColumns();
      if (Array.isArray(visible)) return visible.filter((c) => c.field && c.field !== 'action');
    }
    return allExportColumns;
  }, [allExportColumns]);

  const totalFieldsSet = useMemo(() => new Set([
    'totalClientEstCost', 'totalEstimatedCost', 'totalActualCost',
    'estimatedQuantity', 'actualQuantity', 'variance', 'variancePercent'
  ]), []);


  const getExportableRowsFrom = useCallback((source) => {
    return (source || []).filter(item => !item.isAddRow && !item.isTotalsRow && !item.isNewRow);
  }, []);


  const getExportRows = useCallback(() => {
    const allRows = getExportableRowsFrom(packageData);

    if (allRows.length === 0) {
      return { rows: [], scope: 'all' };
    }

    if (filteredPackageData !== null) {
      return { rows: getExportableRowsFrom(filteredPackageData), scope: 'filtered' };
    }

    return { rows: allRows, scope: 'all' };
  }, [packageData, filteredPackageData, getExportableRowsFrom]);

  // ========== EXPORT TO EXCEL ==========
  const exportToExcel = () => {
    const { rows: validData } = getExportRows();

    if (!validData || validData.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowDropdownMenu(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowDropdownMenu(false);
        return;
      }

      const exportData = validData.map((item) => {
        const row = {};
        exportColumns.forEach(({ field, headerName }) => {
          row[headerName] = formatValueForField(field, item[field]);
        });
        return row;
      });


      const subtotals = calculateTotalsFromRows(validData);
      const totalsRowOut = {};
      exportColumns.forEach(({ field, headerName }, idx) => {
        if (idx === 0) {
          totalsRowOut[headerName] = 'TOTAL';
        } else if (totalFieldsSet.has(field) && field in subtotals) {
          totalsRowOut[headerName] = formatValueForField(field, subtotals[field]);
        } else {
          totalsRowOut[headerName] = '';
        }
      });
      exportData.push(totalsRowOut);

      const ws = XLSX.utils.json_to_sheet(exportData);

      const colWidths = [];
      Object.keys(exportData[0] || {}).forEach((key, index) => {
        const maxLength = Math.max(
          key.length,
          ...exportData.map((row) => { const value = row[key]; return value ? String(value).length : 0; })
        );
        colWidths[index] = { wch: Math.min(maxLength + 2, 50) };
      });
      ws['!cols'] = colWidths;

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'Package Costs');
      XLSX.writeFile(wb, `package-costs.xlsx`);

      setMessage('Export completed successfully!');
      setMessageType('success');
      setShowDropdownMenu(false);

    } catch (error) {
      console.error('Export error:', error);
      setMessage('Error exporting data to Excel');
      setMessageType('error');
      setShowDropdownMenu(false);
    }
  };

  // ========== PRINT TO PDF ==========
  const printToPDF = () => {
    const { rows: validData, scope } = getExportRows();

    if (!validData || validData.length === 0) {
      setMessage('No data to export');
      setMessageType('error');
      setShowDropdownMenu(false);
      return;
    }

    try {
      const exportColumns = getExportColumns();
      if (exportColumns.length === 0) {
        setMessage('No data to export');
        setMessageType('error');
        setShowDropdownMenu(false);
        return;
      }


      const subtotals = calculateTotalsFromRows(validData);

      const doc = new jsPDF({ orientation: 'landscape', unit: 'pt', format: 'a4' });
      const pageWidth = doc.internal.pageSize.getWidth();
      const marginX = 20;
      const cardX = marginX;
      const cardY = 20;
      const cardWidth = pageWidth - marginX * 2;


      const titleText = 'Package Costs'.toUpperCase();

      const subtitleText = jobCode ? `Job  ${jobCode}` : 'PACKAGE COSTS';

      const currentDate = new Date();
      const reportDateStr = `${(currentDate.getMonth() + 1).toString().padStart(2, '0')}/${currentDate.getDate().toString().padStart(2, '0')}/${(currentDate.getFullYear() % 100).toString().padStart(2, '0')}`;

      const headerBarHeight = 60;
      const statsBarHeight = 50;
      const detailsBarHeight = 22;
      const cardHeight = headerBarHeight + statsBarHeight + detailsBarHeight;

      const BLUE_DARK = [30, 75, 120];
      const BLUE_MID = [46, 111, 159];
      const BLUE_DEEP = [22, 58, 95];

      // ── 1) Header bar ─────────────────────────────────────
      doc.setFillColor(...BLUE_DARK);
      doc.rect(cardX, cardY, cardWidth, headerBarHeight, 'F');

      const logoW = 90;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, cardY, logoW, headerBarHeight, 'F');

      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(13);
      doc.text('Rockwell', cardX + logoW / 2, cardY + headerBarHeight / 2 - 2, { align: 'center' });
      doc.setFont('helvetica', 'normal');
      doc.setFontSize(6);
      doc.setTextColor(180, 200, 220);
      doc.text('— SOLUTIONS —', cardX + logoW / 2, cardY + headerBarHeight / 2 + 9, { align: 'center' });

      const titleAreaX = cardX + logoW;
      const titleAreaW = cardWidth - logoW;
      doc.setTextColor(255, 255, 255);
      doc.setFont('helvetica', 'bold');
      doc.setFontSize(18);
      doc.text(titleText, titleAreaX + titleAreaW / 2, cardY + 28, { align: 'center' });

      doc.setFont('helvetica', 'normal');
      doc.setFontSize(8);
      doc.setTextColor(200, 215, 230);
      doc.text(subtitleText, titleAreaX + titleAreaW / 2, cardY + 44, { align: 'center' });

      const metaX = cardX + cardWidth - 12;
      doc.setFontSize(7);
      doc.setTextColor(220, 230, 240);
      doc.setFont('helvetica', 'normal');
      doc.text(`Report: ${reportDateStr}`, metaX, cardY + 16, { align: 'right' });

      const statsY = cardY + headerBarHeight;
      doc.setFillColor(...BLUE_MID);
      doc.rect(cardX, statsY, cardWidth, statsBarHeight, 'F');

      const fmtCurrency = (v) => formatValueForField('totalActualCost', v);
      const stats = [
        { label: 'CLIENT EST. COST', value: fmtCurrency(subtotals.totalClientEstCost) },
        { label: 'TOTAL EST. COST', value: fmtCurrency(subtotals.totalEstimatedCost) },
        { label: 'TOTAL ACT. COST', value: fmtCurrency(subtotals.totalActualCost) },
        { label: 'COST VARIANCE', value: formatValueForField('variance', subtotals.variance) },
        { label: 'VARIANCE %', value: formatValueForField('variancePercent', subtotals.variancePercent) },
      ];

      const statCellW = cardWidth / stats.length;
      stats.forEach((stat, i) => {
        const cellX = cardX + i * statCellW;
        if (i > 0) { doc.setDrawColor(80, 130, 170); doc.setLineWidth(0.5); doc.line(cellX, statsY + 6, cellX, statsY + statsBarHeight - 6); }
        doc.setFont('helvetica', 'normal'); doc.setFontSize(6.5); doc.setTextColor(200, 220, 235);
        doc.text(stat.label, cellX + statCellW / 2, statsY + 15, { align: 'center' });
        doc.setFont('helvetica', 'bold'); doc.setFontSize(11); doc.setTextColor(255, 255, 255);
        doc.text(stat.value, cellX + statCellW / 2, statsY + 35, { align: 'center' });
      });


      const detailsY = statsY + statsBarHeight;
      doc.setFillColor(...BLUE_DEEP);
      doc.rect(cardX, detailsY, cardWidth, detailsBarHeight, 'F');

      doc.setFont('helvetica', 'bold'); doc.setFontSize(9); doc.setTextColor(255, 255, 255);
      doc.text('PACKAGE DETAILS', cardX + 14, detailsY + detailsBarHeight / 2 + 3);

      const scopeLabel = scope === 'filtered' ? 'Filtered' : 'All';
      doc.setFont('helvetica', 'normal'); doc.setFontSize(7); doc.setTextColor(200, 215, 230);
      doc.text(`${scopeLabel} Records: ${validData.length}  ·  Confidential`, cardX + cardWidth - 12, detailsY + detailsBarHeight / 2 + 3, { align: 'right' });


      const tableStartY = cardY + cardHeight + 10;
      const head = [exportColumns.map((c) => c.headerName)];
      const body = validData.map((item) => exportColumns.map(({ field }) => formatValueForField(field, item[field])));
      const footerRow = exportColumns.map(({ field }, idx) => {
        if (idx === 0) return 'TOTAL';
        if (totalFieldsSet.has(field) && field in subtotals) return formatValueForField(field, subtotals[field]);
        return '';
      });

      const rightAlignedFields = ['preInvoiceCost', 'totalClientEstCost', 'estimatedQuantity', 'estimatedCost', 'totalEstimatedCost', 'actualQuantity', 'actualCost', 'totalActualCost', 'actulaCostMFCalc', 'variance', 'variancePercent'];
      const centerAlignedFields = ['clientRateType', 'estimatedRateType', 'actualRateType', 'isCostFinal'];

      autoTable(doc, {
        head, body, foot: [footerRow], startY: tableStartY,
        margin: { left: marginX, right: marginX },
        styles: { fontSize: 5, cellPadding: 3, overflow: 'linebreak', valign: 'middle' },
        headStyles: { fillColor: [46, 111, 159], textColor: [255, 255, 255], fontStyle: 'bold', fontSize: 5 },
        footStyles: { fillColor: [240, 248, 255], textColor: [0, 0, 0], fontStyle: 'bold' },
        alternateRowStyles: { fillColor: [250, 250, 250] },
        didParseCell: (data) => {
          if (data.section === 'body' || data.section === 'foot') {
            const col = exportColumns[data.column.index];
            if (col) {
              if (rightAlignedFields.includes(col.field)) data.cell.styles.halign = 'right';
              else if (centerAlignedFields.includes(col.field)) data.cell.styles.halign = 'center';
            }
          }
        },
        didDrawPage: () => {
          const pageCount = doc.internal.getNumberOfPages();
          const pageCurrent = doc.internal.getCurrentPageInfo().pageNumber;
          const pageHeight = doc.internal.pageSize.getHeight();
          doc.setFontSize(8); doc.setTextColor(120, 120, 120);
          doc.text(`Page ${pageCurrent} of ${pageCount}`, pageWidth - marginX, pageHeight - 10, { align: 'right' });
        },
      });

      doc.save(`package-costs-${new Date().toISOString().split('T')[0]}.pdf`);

      setMessage('Export completed successfully!');
      setMessageType('success');
      setShowDropdownMenu(false);

    } catch (error) {
      console.error('Error exporting to PDF:', error);
      setMessage('Error exporting data to PDF');
      setMessageType('error');
      setShowDropdownMenu(false);
    }
  };

  const handleJobDetailsClick = (e) => {
    e.preventDefault();
    navigate(`/job-details/${jobCode}`, {
      state: {
        jobId: activeJobId,
        jobCode,
        clientName,
        source: navigationSource,
        isAllClients: isAllClientsFlow,
        ...(cameFromIntegration ? { fromIntegration: true } : {}),
        ...(reportsContext?.fromReports
          ? {
            fromReports: true,
            reportFilters: reportsContext.reportFilters,
            selectedClientId: reportsContext.selectedClientId,
            reportType: reportsContext.reportType,
            rowData: reportsContext.rowData
          }
          : {})
      }
    });
  };

  const handleJobListsClick = (e) => {
    e.preventDefault();

    navigate('/job-module', {
      state: {
        clientId: clientId,
        source: navigationSource,
        isAllClients: isAllClientsFlow
      }
    });
  };

  const toggleDropdownMenu = () => setShowDropdownMenu(!showDropdownMenu);

  const handleCostTypeChange = (type) => {
    setSelectedCostType(type);
    setShowCostTypeDropdown(false);
    switch (type) {
      case 'postage': navigate('/postage-details', { state: { jobId: activeJobId, jobCode, clientName } }); break;
      case 'package': navigate('/package-costs-details', {
        state: {
          jobId: activeJobId, jobCode, clientName, source: navigationSource,

          isAllClients: isAllClientsFlow
        }
      }); break;
      case 'backend': navigate('/backend-costs-details', { state: { jobId: activeJobId, jobCode, clientName } }); break;
      case 'lists': navigate('/lists-details', { state: { jobId: activeJobId, jobCode, clientName } }); break;
      default: break;
    }
  };


  useEffect(() => { setFilteredPackageData(null); }, [packageData]);

  const columnDefs = useMemo(() => [
    {
      field: 'packageName', headerName: 'Cost Type', width: 220, minWidth: 180,
      filter: 'agTextColumnFilter', sortable: true, pinned: 'left',
      lockPinned: true,
      cellRenderer: (params) => {
        if (params.data?.isAddRow) return <span style={{ backgroundColor: '#ffffff', cursor: 'pointer', color: '#007bff', fontWeight: '500' }}>+ New Component</span>;
        if (params.data?.isTotalsRow) return <span></span>;
        const isNewRow = params.data?.isNewRow || (params.data?.tempId && String(params.data.tempId).startsWith('temp-'));
        if (isEditMode && isNewRow) {
          return (<CostTypeDropdown value={params.value} id={params.data?.tempId || params.data?.jobCostID} rowData={params.data} costTypeOptions={costTypeOptions} isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
        }
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID} field="packageName" placeholder="Required *" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: (params) => {
        if (params.data?.isAddRow) return { backgroundColor: '#ffffff', cursor: 'pointer', color: '#007bff', fontWeight: '500' };
        if (params.data?.isTotalsRow) return { backgroundColor: '#f0f8ff' };
        if (params.data?.isNewRow && (!params.value || params.value.trim() === '')) return { fontWeight: '500', backgroundColor: '#ffebee', overflow: 'visible' };
        return { fontWeight: '500', backgroundColor: '#ffffff', overflow: 'visible' };
      }
    },
    {
      field: 'packageDescription', headerName: 'Description', minWidth: 200, flex: 1.5,
      filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>TOTAL</span>;
        if (params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="packageDescription" placeholder="Enter description" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: (params) => { if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff' }; return null; }
    },
    {
      field: 'preInvoiceNumber', headerName: 'Client Est. Inv #', minWidth: 200, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="preInvoiceNumber" placeholder="Invoice #" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'preInvoiceCost', headerName: 'Client Estimate', minWidth: 200, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="preInvoiceCost" type="number" isCurrency={true} placeholder="0.00" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'clientRateType', headerName: 'M/F', minWidth: 120, maxWidth: 150, filter: 'agTextColumnFilter', sortable: false, cellStyle: { textAlign: 'center' },
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<MFRateDropdown value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="clientRateType" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'totalClientEstCost', headerName: 'Total Client Est. Cost', minWidth: 220, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <CurrencyRenderer value={params.value} isTotal={true} />;
        if (params.data?.isAddRow) return <span></span>;
        return <CurrencyRenderer value={params.value} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'poNumber', headerName: 'PO Number', minWidth: 200, sortable: true, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="poNumber" placeholder="PO Number" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'vendor', headerName: 'Vendor', minWidth: 150, sortable: true, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="vendor" placeholder="Vendor name" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'invoiceNumber', headerName: 'Invoice Number', minWidth: 200, sortable: true, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="invoiceNumber" placeholder="Invoice #" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'estimatedQuantity', headerName: 'Est. Quantity', minWidth: 180, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
        if (params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="estimatedQuantity" type="number" placeholder="0" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right' };
      }
    },
    {
      field: 'estimatedCost', headerName: 'Est. Cost', minWidth: 200, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="estimatedCost" type="number" isCurrency={true} placeholder="0.00" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'estimatedRateType', headerName: 'M/F', minWidth: 120, maxWidth: 150, filter: 'agTextColumnFilter', sortable: false, cellStyle: { textAlign: 'center' },
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<MFRateDropdown value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="estimatedRateType" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'totalEstimatedCost', headerName: 'Total Est. Cost', minWidth: 200, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <CurrencyRenderer value={params.value} isTotal={true} />;
        if (params.data?.isAddRow) return <span></span>;
        return <CurrencyRenderer value={params.value} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'actualQuantity', headerName: 'Act. Quantity', minWidth: 200, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <span style={{ fontWeight: 'bold' }}>{formatNumber(params.value)}</span>;
        if (params.data?.isAddRow) return <span></span>;
        const displayValue = params.value === null || params.value === undefined ? '' : formatNumber(params.value);
        return <span>{displayValue}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'actualCost', headerName: 'Act. Cost', minWidth: 200, filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<EditableInputRenderer value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="actualCost" type="number" isCurrency={true} placeholder="0.00" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      },
      cellStyle: { textAlign: 'right' }
    },
    {
      field: 'actualRateType', headerName: 'M/F', minWidth: 120, maxWidth: 150, filter: 'agTextColumnFilter', sortable: false, cellStyle: { textAlign: 'center' },
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        return (<MFRateDropdown value={params.value} id={params.data?.jobCostID || params.data?.tempId} field="actualRateType" isEditMode={isEditMode} onFieldChange={handleFieldChange} />);
      }
    },
    {
      field: 'totalActualCost', headerName: 'Total Act. Cost', minWidth: 210, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <CurrencyRenderer value={params.value} isTotal={true} />;
        if (params.data?.isAddRow) return <span></span>;
        if (params.value === null || params.value === undefined || params.value === '') return <span style={{ color: '#6c757d' }}>0</span>;
        return <CurrencyRenderer value={params.value} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'isCostFinal', headerName: 'Status', minWidth: 150, width: 150, filter: 'agTextColumnFilter', sortable: false,
      cellRenderer: (params) => (<StatusDropdownCell data={params.data} isEditMode={isEditMode} onStatusChange={handleStatusChange} />)
    },
    {
      field: 'actulaCostMFCalc', headerName: '/M', width: 100, minWidth: 100,
      valueFormatter: (params) => formatNumber(params.value), filter: 'agTextColumnFilter', sortable: true,
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow || params.data?.isAddRow) return <span></span>;
        const displayValue = (params.value === null || params.value === undefined) ? 0 : params.value;
        return <span>{formatNumber(displayValue)}</span>;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'variance', headerName: 'Cost Variance', minWidth: 200, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <VarianceRenderer value={params.value} isTotal={true} />;
        if (params.data?.isAddRow) return <span></span>;
        return <VarianceRenderer value={params.value} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    },
    {
      field: 'variancePercent', headerName: 'Cost Variance %', minWidth: 400, filter: 'agTextColumnFilter',
      cellRenderer: (params) => {
        if (params.data?.isTotalsRow) return <VariancePercentRenderer value={params.value} isTotal={true} />;
        if (params.data?.isAddRow) return <span></span>;
        return <VariancePercentRenderer value={params.value} />;
      },
      cellStyle: (params) => {
        if (params.data?.isTotalsRow) return { fontWeight: 'bold', backgroundColor: '#f0f8ff', textAlign: 'right' };
        return { textAlign: 'right', backgroundColor: '#ffffff' };
      }
    }
  ], [isEditMode, handleFieldChange, handleStatusChange, costTypeOptions]);

  const getRowId = useCallback((params) => {
    if (params.data?.isTotalsRow) return 'totals-row';
    if (params.data?.isAddRow) return 'add-row';
    if (params.data?.jobCostID) return String(params.data.jobCostID);
    if (params.data?.tempId) return String(params.data.tempId);
    return undefined;
  }, []);

  if (loading) return <AisScreenLoader />;

  return (
    <>
      <div className='main-container-postage-details'>
        <div className="mail-container-postage">
          {/* Breadcrumb Navigation */}
          <div className="job-details-breadcrumb px-0 pb-4">
            <nav className="breadcrumb-nav">

              <ol className="breadcrumb-list">


                {showReportsCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="/reports"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/reports', {
                            state: {
                              restoreReports: true,
                              reportFilters: reportsContext.reportFilters || {},
                              selectedClientId: reportsContext.selectedClientId || [],
                              reportType: reportsContext.reportType || '',
                              rowData: reportsContext.rowData || []
                            }
                          });
                        }}
                      >
                        Reports
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* ✅ Client Select */}
                {showClientSelectCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <Icon
                        name="user-icon"
                        size={14}
                        alt="user-icon"
                        className="breadcrumb-icon"
                      />
                      <a
                        href="/dashboard"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          sessionStorage.removeItem('jobDetailsSource');
                          sessionStorage.removeItem('isAllClientsFlow');
                          navigate('/dashboard');
                        }}
                      >
                        Client Select
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* ✅ Client */}
                {showClientNameCrumb && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="/client-dashboard"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/client-dashboard', {
                            state: {
                              clientId: clientId,
                              source: 'client-dashboard',
                              isAllClients: false
                            }
                          });
                        }}
                      >
                        Client: {clientName}
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {/* Integration crumb — client flow (Tallies -> LoL -> Job -> Cost -> Package) */}
                {cameFromIntegration && navigationSource === 'client-dashboard' && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="#"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          navigate('/talliesSection', {
                            state: {
                              activeTab: 'lists',
                              clientId: clientId,
                              clientName: clientName
                            }
                          });
                        }}
                      >
                        Integration
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}

                {navigationSource === 'list-upload' && (
                  <>
                    <li className="breadcrumb-item">
                      <a
                        href="#"
                        className="breadcrumb-link"
                        onClick={(e) => {
                          e.preventDefault();
                          sessionStorage.removeItem('jobDetailsSource');
                          sessionStorage.removeItem('isAllClientsFlow');
                          navigate('/talliesSection', {
                            state: {
                              activeTab: 'lists'
                            }
                          });
                        }}
                      >
                        Integration
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}




                {/* ✅ Job Lists (ONLY when coming from job module) */}
                {navigationSource === 'job-module' && !showClientSelectCrumb && (
                  <>
                    <li className="breadcrumb-item d-flex align-items-center">
                      <span style={{ paddingRight: "4px" }}>
                        <Icon name="mail-icon" size={14} alt="mail-icon" />
                      </span>
                      <a
                        href="/job-module"
                        className="breadcrumb-link"
                        onClick={handleJobListsClick}
                      >
                        Job Lists
                      </a>
                    </li>
                    <li className="breadcrumb-separator">/</li>
                  </>
                )}


                <li className="breadcrumb-item">
                  <a
                    href={`/job-details/${activeJobId}`}
                    className="breadcrumb-link"
                    onClick={handleJobDetailsClick}
                  >
                    Job Details: {jobCode}
                  </a>
                </li>

                <li className="breadcrumb-separator">/</li>


                <li className="breadcrumb-item dropdown-container" style={{ position: 'relative' }}>
                  <div
                    className="breadcrumb-dropdown-trigger"
                    onClick={() => setShowCostTypeDropdown(!showCostTypeDropdown)}
                    style={{
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '5px',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      backgroundColor: showCostTypeDropdown ? '#e9ecef' : 'transparent'
                    }}
                  >
                    <span className="active">
                      {selectedCostType === 'postage' && 'Postage'}
                      {selectedCostType === 'package' && 'Package Costs'}
                      {selectedCostType === 'backend' && 'Backend Costs'}
                      {selectedCostType === 'lists' && 'Lists'}
                    </span>
                    <Icon name="expand-open" size={16} />
                  </div>

                  {showCostTypeDropdown && (
                    <div
                      className="cost-type-dropdown-menu"
                      style={{
                        position: 'absolute',
                        top: '100%',
                        left: '0',
                        backgroundColor: 'white',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                        zIndex: 1000,
                        minWidth: '150px'
                      }}
                    >
                      <div
                        className="dropdown-option"
                        onClick={() => handleCostTypeChange('postage')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedCostType === 'postage' ? '#f0f8ff' : 'white'
                        }}
                      >
                        Postage
                      </div>

                      <div
                        className="dropdown-option"
                        onClick={() => handleCostTypeChange('package')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedCostType === 'package' ? '#f0f8ff' : 'white'
                        }}
                      >
                        Package Costs
                      </div>

                      <div
                        className="dropdown-option"
                        onClick={() => handleCostTypeChange('backend')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedCostType === 'backend' ? '#f0f8ff' : 'white'
                        }}
                      >
                        Backend Costs
                      </div>

                      <div
                        className="dropdown-option"
                        onClick={() => handleCostTypeChange('lists')}
                        style={{
                          padding: '8px 12px',
                          cursor: 'pointer',
                          backgroundColor: selectedCostType === 'lists' ? '#f0f8ff' : 'white'
                        }}
                      >
                        Lists
                      </div>
                    </div>
                  )}
                </li>
              </ol>

            </nav>
          </div>


          {/* Package Grid Section */}
          <div className="postage-grid-container">
            <div className="postage-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
              <h3 className="postage-title" style={{ margin: 0 }}>{jobCode} Package Costs</h3>
            </div>

            <div className="d-flex justify-content-between align-items-center mb-4">
              <div className="left-side-buttons">
                {!isManageComponentsActive && (
                  <button className="manage-mail-codes-btn" onClick={handleManageComponentsClick}>Manage Components</button>
                )}
              </div>

              <div className="job-details-menu-container" ref={menuDropdownRef}>
                {isManageComponentsActive ? (
                  <div className="action-buttons d-flex gap-2">
                    <button className="delete-btn btn btn-danger btn-sm" onClick={handleDeleteClick}>Delete</button>
                    <button className="cancel-delete-btn" onClick={handleCancelClick}>Cancel</button>
                    <button className="save-changes-btn" onClick={handleSaveChangesClick}>Save Changes</button>
                  </div>
                ) : (
                  <div style={{ position: 'relative' }}>
                    <button className="job-details-menu-buttons" onClick={toggleDropdownMenu}>
                      <span className="menu-dot"></span>
                      <span className="menu-dot"></span>
                      <span className="menu-dot"></span>
                    </button>

                    {showDropdownMenu && (
                      <div className="dropdown-menu-container" style={{ position: 'absolute', top: '100%', right: 0, backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '4px', boxShadow: '0 2px 8px rgba(0,0,0,0.15)', zIndex: 1000, minWidth: '160px', overflow: 'hidden' }}>
                        <button className="dropdown-menu-item"
                          onClick={() => { exportToExcel(); setShowDropdownMenu(false); }}
                          style={{ width: '100%', padding: '10px 16px', textAlign: 'left', background: 'none', border: 'none', borderBottom: '1px solid #f0f0f0', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                            <polyline points="14 2 14 8 20 8" /><line x1="8" y1="13" x2="16" y2="13" />
                            <line x1="8" y1="17" x2="16" y2="17" /><polyline points="10 9 9 9 8 9" />
                          </svg>
                          Export to Excel
                        </button>
                        <button className="dropdown-menu-item"
                          onClick={() => { printToPDF(); setShowDropdownMenu(false); }}
                          style={{ width: '100%', padding: '10px 16px', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', fontSize: '14px', display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="6 9 6 2 18 2 18 9" />
                            <path d="M6 18H4a2 2 0 0 1-2-2v-5a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v5a2 2 0 0 1-2 2h-2" />
                            <rect x="6" y="14" width="12" height="8" />
                          </svg>
                          Print To PDF
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <AgGrid
              ref={agGridRef}
              rowData={rowDataWithTotals}
              pinnedBottomRowData={[totalsRow]}
              onCellClicked={handleCellClicked}
              columnDefs={columnDefs}
              onGridReady={(params) => { gridApiRef.current = params.api; setGridApi(params.api); }}
              onFilterChanged={handleFilterChanged}
              hideSideBar={true}
              hideSelectAll={!isManageComponentsActive}
              gridOptions={{
                getRowId,
                defaultColDef: { resizable: true, sortable: true, filter: true },
                rowSelection: isManageComponentsActive ? 'multiple' : undefined,
                suppressRowClickSelection: true,
                isRowSelectable: (params) => !params.data?.isTotalsRow && !params.data?.isAddRow,
                getRowStyle: (params) => {
                  if (params.data?.isTotalsRow) return { backgroundColor: '#f0f8ff', fontWeight: 'bold' };
                  if (params.data?.isAddRow) return { backgroundColor: '#ffffff', cursor: 'pointer' };
                  if (params.data?.isNewRow) return { backgroundColor: '#fff' };
                  return null;
                }
              }}
            />
          </div>
        </div>


        {showDeleteConfirm && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', justifyContent: 'center', alignItems: 'center', zIndex: 1000 }}>
            <div style={{ backgroundColor: 'white', padding: '20px', borderRadius: '8px', maxWidth: '400px', width: '90%' }}>
              <h3 style={{ marginBottom: '15px' }}>Confirm Delete</h3>
              <p style={{ marginBottom: '20px' }}>Are you sure you want to delete {selectedCostsToDelete.length} item(s)?</p>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '10px' }}>
                <button onClick={handleCancelDelete} disabled={deleteInProgress} style={{ padding: '8px 16px', backgroundColor: '#f0f0f0', border: 'none', borderRadius: '4px', cursor: 'pointer' }}>Cancel</button>
                <button onClick={handleConfirmDelete} disabled={deleteInProgress} style={{ padding: '8px 16px', backgroundColor: '#dc3545', color: 'white', border: 'none', borderRadius: '4px', cursor: deleteInProgress ? 'not-allowed' : 'pointer' }}>
                  {deleteInProgress ? 'Deleting...' : 'Delete'}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {message && (
        <SuccessMessage
          message={message}
          type={messageType}
          onClose={() => { setMessage(''); setMessageType('success'); }}
        />
      )}
    </>
  );
};

export default PackageCostsDetails;
